import React, { useState, useEffect, useMemo } from 'react';

// Price Optimization Banner Component
const PriceOptimizationBanner = ({ 
  suggestions, 
  onApplySuggestion, 
  formatCurrency, 
  selectedProducts, 
  onlyDoorProducts, 
  preEngDoorProducts 
}) => {
  const [showAllOptimizations, setShowAllOptimizations] = useState(false);
  
  if (suggestions.length === 0) return null;
  
  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 bg-blue-100 rounded-full p-1 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">Price Optimization Opportunity</h3>
            <div className="mt-1 text-sm text-blue-700">
              <p>You can save on your order by standardizing product selections:</p>
              <ul className="mt-2 space-y-1">
                {suggestions.slice(0, 2).map((suggestion, index) => (
                  <li key={index} className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 mr-1 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>
                      Use <span className="font-medium">{suggestion.productName}</span> in {suggestion.areasToChange.length} more area{suggestion.areasToChange.length > 1 ? 's' : ''} to save <span className="font-medium">₹{formatCurrency(suggestion.potentialSavings)}</span>
                      <span className="block text-xs mt-0.5 text-blue-600">
                        (Price drops from ₹{formatCurrency(suggestion.currentPrice)} to ₹{formatCurrency(suggestion.potentialPrice)})
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="mt-3 flex space-x-2">
              <button
                onClick={() => onApplySuggestion(suggestions[0])}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                Apply Best Suggestion
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {suggestions.length > 2 && (
                <button 
                  className="px-3 py-1.5 border border-blue-300 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-50 flex items-center"
                  onClick={() => setShowAllOptimizations(true)}
                >
                  See {suggestions.length - 2} more suggestions
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* All Optimization Suggestions Modal */}
      {showAllOptimizations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto animate-fade-in">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">All Price Optimization Suggestions</h3>
              <button
                onClick={() => setShowAllOptimizations(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              <div className="space-y-4">
                {suggestions.map((suggestion, index) => {
                  // Find the product details
                  let product = null;
                  if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
                    product = onlyDoorProducts.find(p => p._id === suggestion.productId);
                  }
                  if (!product && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
                    product = preEngDoorProducts.find(p => p._id === suggestion.productId);
                  }
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 p-3 border-b border-gray-200 flex justify-between items-center">
                        <div className="flex items-center">
                          {product?.vendor?.image && (
                            <div className="border border-gray-200 rounded-md p-0.5 bg-white mr-2">
                              <img
                                src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + product.vendor.image}
                                alt={product.vendor?.name || "Vendor"}
                                className="w-5 h-5 object-contain"
                              />
                            </div>
                          )}
                          <span className="font-medium">{suggestion.productName}</span>
                        </div>
                        <div className="text-green-600 font-medium">
                          Save ₹{formatCurrency(suggestion.potentialSavings)}
                        </div>
                      </div>
                      
                      <div className="p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Current Price:</span>
                                <span>₹{formatCurrency(suggestion.currentPrice)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">New Price:</span>
                                <span className="text-green-600">₹{formatCurrency(suggestion.potentialPrice)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Current Quantity:</span>
                                <span>{suggestion.currentQuantity}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">New Quantity:</span>
                                <span>{suggestion.potentialQuantity}</span>
                              </div>
                              {product?.addons?.length > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Add-ons:</span>
                                  <span>{product.addons.length} groups</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Areas to Change</h4>
                            <div className="space-y-1">
                              {suggestion.areasToChange.map((areaName, areaIndex) => {
                                // Find the current product for this area
                                const currentProductId = selectedProducts[areaName];
                                let currentProduct = null;
                                
                                // Look in onlyDoorProducts
                                if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
                                  currentProduct = onlyDoorProducts.find(p => p._id === currentProductId);
                                }
                                
                                // If not found, look in preEngDoorProducts
                                if (!currentProduct && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
                                  currentProduct = preEngDoorProducts.find(p => p._id === currentProductId);
                                }
                                
                                return (
                                  <div key={areaIndex} className="text-sm flex justify-between">
                                    <span className="text-gray-600">{areaName}</span>
                                    {currentProduct && (
                                      <span className="text-gray-500 truncate max-w-[150px]" title={currentProduct.product_group_name}>
                                        {currentProduct.product_group_name}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        
                        {/* Addon changes warning if needed */}
                        {product?.addons?.length > 0 && suggestion.addonChanges && (
                          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-md">
                            <div className="flex items-start">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-500 mt-0.5 mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <div className="text-xs text-amber-700">
                                <p className="font-medium">Note: Add-on configurations will change</p>
                                <p className="mt-1">Some areas will have different add-ons when standardizing to this product.</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => {
                              onApplySuggestion(suggestion);
                              setShowAllOptimizations(false);
                            }}
                            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                          >
                            Apply This Suggestion
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end">
              <button
                onClick={() => setShowAllOptimizations(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Selected Product Display Component
const SelectedProductDisplay = ({
  product,
  formatCurrency,
  onChangeClick,
  doorSubcategories
}) => {
  if (!product) return null;
  
  // Calculate total price including addons
  const calculateTotalPrice = () => {
    // Start with base product price
    let totalPrice = parseFloat(product.selling_price || 0);
    
    // Add prices from addons if they exist
    if (product.addons && Array.isArray(product.addons)) {
      product.addons.forEach(addon => {
        if (addon.addon_groups && Array.isArray(addon.addon_groups)) {
          addon.addon_groups.forEach(addonGroup => {
            // Find matching subcategory
            const matchingSubcategoryId = addonGroup.product_sub_category_id;
            const matchingSubcategory = doorSubcategories && doorSubcategories.find(
              subcategory => subcategory.subcategory && subcategory.subcategory._id === matchingSubcategoryId
            );
            
            // Get matching vendor ID
            const matchingVendorId = matchingSubcategory && matchingSubcategory.defaultVendor &&
              matchingSubcategory.defaultVendor._id;
            
            // If we have matching vendor and products, add their prices
            if (matchingVendorId && addonGroup.products && Array.isArray(addonGroup.products)) {
              const matchingProducts = addonGroup.products.filter(p => p.vendor_id === matchingVendorId);
              //console.log("Matching Products:", matchingProducts);
              matchingProducts.forEach(addonProduct => {
                if (addonProduct.selling_price) {
                  totalPrice += parseFloat(addonProduct.selling_price);
                }
              });
            }
          });
        }
      });
    }
    
    // Also check product_addons array if it exists (different structure)
    if (product.product_addons && Array.isArray(product.product_addons)) {
      product.product_addons.forEach(addon => {
        // Only include non-hardware addons in the price calculation
        if (addon.product_addon_type?.toLowerCase() !== 'hardware') {
          totalPrice += parseFloat(addon.selling_price || 0);
        }
      });
    }
    
    return totalPrice;
  };
  
  // Get the total price including addons
  const totalPrice = calculateTotalPrice();
  
 return (
  <div className="flex flex-col">
    {/* Selected Product Card */}
    <div className="border rounded-lg overflow-hidden shadow-sm bg-white">
      <div className="px-4 py-3">
        {/* Product Header with Vendor and Selection */}
        <div className="flex items-start justify-between mb-3">
          {/* Vendor logo and product name */}
          <div className="flex items-start flex-1">
            {product.vendor?.image && (
              <div className="border border-gray-200 rounded-md p-1 bg-white mr-3 flex-shrink-0">
                <img
                  src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + product.vendor.image}
                  alt={product.vendor?.name || "Vendor"}
                  className="w-8 h-8 object-contain"
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h4
                className="text-sm font-semibold text-gray-800 line-clamp-2"
                title={product.item_name || product.product_group_name || "Item Name"}
              >
                {product.item_name || product.product_group_name || "Item Name"}
              </h4>
              
              {/* Add vendor name below product name */}
              {product.vendor?.name && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {product.vendor.name}
                </p>
              )}
            </div>
          </div>
          
          {/* Price badge - Now showing total price including addons */}
          <div className="bg-[#F0FFF0] text-[#14A311] text-xs font-medium px-3 py-1.5 rounded-md flex items-center ml-3 flex-shrink-0">
            <span className="font-semibold">₹{formatCurrency(totalPrice)}</span>
            <span className="mx-1 text-[#14A311]/70">/</span>
            <span className="text-[#14A311]/90">{product?.UOM || "sq.ft"}</span>
          </div>
        </div>
        
        {/* Display addons if available */}
        {product.addons && product.addons.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-600 mb-2">Part of this door:</p>
            <div className="flex flex-col gap-2">
              {product.addons.map((addon, addonIndex) => {
                return (
                  <div key={addonIndex} className="rounded-md overflow-hidden border border-gray-200 bg-gray-50 w-full">
                    {/* Addon products */}
                    <div className="px-3 py-2">
                      {addon.addon_groups && addon.addon_groups.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {addon.addon_groups.map((addonGroup, addonGroupIndex) => {
                            // Try to find matching subcategory ID
                            const matchingSubcategoryId = addonGroup.product_sub_category_id;
                            
                            // Find the matching subcategory object
                            const matchingSubcategory = doorSubcategories && doorSubcategories.find(
                              (subcategory) => subcategory.subcategory && subcategory.subcategory._id === matchingSubcategoryId
                            );
                            
                            // Get the matching vendor ID if subcategory exists
                            const matchingVendorId = matchingSubcategory && matchingSubcategory.defaultVendor &&
                              matchingSubcategory.defaultVendor._id;
                            
                            // Only proceed if we have both subcategory and vendor
                            if (matchingSubcategoryId && matchingVendorId) {
                              const matchingProducts = addonGroup.products && addonGroup.products.length > 0
                                ? addonGroup.products.filter(p => p.vendor_id === matchingVendorId)
                                : [];
                              
                              return (
                                <div key={addonGroupIndex} className="flex flex-col">
                                  {/* Subcategory name */}
                                  {matchingSubcategory && (
                                    <div className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
                                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5"></div>
                                      {matchingSubcategory.subcategory.product_sub_category_name}
                                    </div>
                                  )}
                                  
                                  {/* Products list with individual backgrounds and prices */}
                                  <div className="w-full">
                                    {matchingProducts.length > 0 ? (
                                      <div className="flex flex-wrap gap-1.5">
                                        {matchingProducts.map((addonProduct, productIndex) => (
                                          <span
                                            key={`${addonGroupIndex}-${productIndex}`}
                                            className="inline-block px-2.5 py-1 rounded-md text-xs bg-white text-gray-700 font-medium truncate max-w-full border border-gray-200"
                                            title={`${addonProduct.product_name} - ₹${formatCurrency(addonProduct.selling_price || 0)}`}
                                          >
                                            {addonProduct.product_name}
                                            {addonProduct.selling_price ?
                                              <span className="ml-1 text-[#14A311]">
                                                ₹{formatCurrency(addonProduct.selling_price)}
                                              </span> : ''}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="inline-block px-2.5 py-1 rounded-md text-xs bg-white text-gray-700 font-medium border border-gray-200">
                                        {addonGroup.product_name || matchingSubcategory.subcategory.product_sub_category_name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            // Fallback if no matching subcategory or vendor
                            return (
                              <div key={addonGroupIndex} className="flex items-center">
                                <span className="inline-block px-2.5 py-1 rounded-md text-xs bg-white text-gray-700 font-medium border border-gray-200">
                                  {addonGroup.product_name || "Addon"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Display product_addons if available (alternative structure) */}
        {product.product_addons && product.product_addons.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-600 mb-2">Includes:</p>
            <div className="flex flex-col gap-2">
              <div className="rounded-md overflow-hidden border border-gray-200 bg-gray-50 w-full">
                <div className="px-3 py-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {product.product_addons
                      .filter(addon => addon.product_addon_type?.toLowerCase() !== 'hardware')
                      .map((addon, addonIndex) => {
                        return (
                          <div key={addonIndex} className="flex flex-col">
                            <div className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5"></div>
                              {addon.item_sub_category_name || 'Addon'}
                            </div>
                            <div className="w-full">
                              <span
                                className="inline-block px-2.5 py-1 rounded-md text-xs bg-white text-gray-700 font-medium border border-gray-200"
                                title={`${addon.item_name} - ₹${formatCurrency(addon.selling_price || 0)}`}
                              >
                                {addon.item_name}
                                {addon.selling_price ?
                                  <span className="ml-1 text-[#14A311]">
                                    ₹{formatCurrency(addon.selling_price)}
                                  </span> : ''}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    
    {/* Change Button - Moved to the right */}
    <div className="flex justify-end mt-3">
      <button
        className="text-[#F30F0F] text-xs font-medium px-3 py-1.5 border border-[#F30F0F] rounded-md hover:bg-gray-100 flex items-center"
        onClick={onChangeClick}
      >
       {/*  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg> */}
        Change
      </button>
    </div>
  </div>
);

};


// Empty State Component
const EmptyProductState = ({ onSelectClick }) => {
  return (
    <div className="flex flex-col items-center justify-center py-6 bg-gray-50 rounded-lg border border-gray-200">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
      <p className="text-gray-500 text-sm mb-3">No product selected for this area</p>
      <button 
        className="px-4 py-2 bg-[#C20C0C] text-white text-sm font-medium rounded-lg hover:bg-[#A00A0A] transition-colors"
        onClick={onSelectClick}
      >
        Select Product
      </button>
    </div>
  );
};

// Product Selection Popup Component
const ProductSelectionPopup = ({
  isOpen,
  onClose,
  areaName,
  areaData,
  selectedDoorType,
  onDoorTypeChange,
  tempSelectedProductId,
  onProductSelect,
  onApply,
  isApplying,
  productApplied,
  formatCurrency,
  selectedProducts,
  getPriceBasedOnMoq,
  onlyDoorProducts,
  preEngDoorProducts,
  doorSubcategories,
  data
}) => {
  if (!isOpen) return null;
  
  // Find the currently selected product for this area
  const currentProductId = selectedProducts[areaName];
  let currentProduct = null;
  
  // Look for the current product in available product lists
  if (onlyDoorProducts?.length) {
    currentProduct = onlyDoorProducts.find(p => p._id === currentProductId);
  }
  if (!currentProduct && preEngDoorProducts?.length) {
    currentProduct = preEngDoorProducts.find(p => p._id === currentProductId);
  }
  
  // Find the temporarily selected product
  let tempSelectedProduct = null;
  if (tempSelectedProductId && areaData?.doorTypes?.[selectedDoorType]) {
    tempSelectedProduct = areaData.doorTypes[selectedDoorType].find(
      item => item.item_group_id === tempSelectedProductId
    );
  }
  
  // Helper function to calculate addon costs for a product
  const calculateAddonCost = (product) => {
    if (!product || !product.addons || !Array.isArray(product.addons)) {
      return 0;
    }
    
    let totalAddonCost = 0;
    
    // Process addons array (standard structure)
   product.addons.forEach(addon => {
  if (addon.addon_groups && Array.isArray(addon.addon_groups)) {
    addon.addon_groups.forEach(addonGroup => {
      if (addonGroup.products && Array.isArray(addonGroup.products)) {
        // Find the matching subcategory from doorSubcategories
        const matchingSubcategory = doorSubcategories && doorSubcategories.find(
          subcategory => subcategory.subcategory && 
                         subcategory.subcategory._id === addonGroup.product_sub_category_id
        );
        
        // Get the default vendor ID from the matching subcategory
        const defaultVendorId = matchingSubcategory && 
                               matchingSubcategory.defaultVendor && 
                               matchingSubcategory.defaultVendor._id;
        
        // Filter products by the default vendor from the subcategory
        const vendorProducts = defaultVendorId ? 
          addonGroup.products.filter(p => p.vendor_id === defaultVendorId) : 
          // If no default vendor found, use the first product as fallback
          addonGroup.products.length > 0 ? [addonGroup.products[0]] : [];
        
        // Add up the prices of all vendor products
        vendorProducts.forEach(addonProduct => {
          if (addonProduct.selling_price) {
            totalAddonCost += parseFloat(addonProduct.selling_price);
          }
        });
      }
    });
  }
});

    // Also check product_addons array if it exists (alternative structure)
    if (product.product_addons && Array.isArray(product.product_addons)) {
      product.product_addons.forEach(addon => {
        // Only include non-hardware addons in the price calculation
        if (addon.product_addon_type?.toLowerCase() !== 'hardware' && addon.selling_price) {
          totalAddonCost += parseFloat(addon.selling_price);
        }
      });
    }
    
    return totalAddonCost;
  };
  
  // Get addon costs for current and temp products
  const currentProductAddonCost = currentProduct ? calculateAddonCost(currentProduct) : 0;
  const tempProductAddonCost = tempSelectedProduct ? calculateAddonCost(tempSelectedProduct) : 0;
  
  const ItemCurrentQuantity = currentProduct?.UOM && currentProduct?.UOM?.toLowerCase() === 'no' ? areaData.count : areaData.area || 1;
  if (currentProduct) {
    currentProduct.quantity = ItemCurrentQuantity;
  }
  
  // Calculate price comparison if both current and temp products exist
  let priceComparison = null;
  if (currentProduct && tempSelectedProduct && areaData) {
    const Uom = currentProduct.UOM;
    const NewUOM = tempSelectedProduct.UOM;
    const quantity = Uom && Uom.toLowerCase() === 'no' ? areaData.count : areaData.area || 1;
    const newQuantity = NewUOM && NewUOM.toLowerCase() === 'no' ? areaData.count : areaData.area || 1;
    tempSelectedProduct.quantity = quantity;
    
    // Get base prices from MOQ calculation
    const currentBasePrice = getPriceBasedOnMoq(currentProduct, quantity, data.items);
    const newBasePrice = tempSelectedProduct.selling_price || 0;
    
    // Calculate total prices including addons
    const currentPrice = currentBasePrice + currentProductAddonCost;
    const newPrice = newBasePrice + tempProductAddonCost;
    
    // Calculate totals
    const currentTotal = currentPrice * quantity;
    const newTotal = newPrice * newQuantity;
    const priceDifference = newTotal - currentTotal;
    
    priceComparison = {
      currentBasePrice,
      newBasePrice,
      currentAddonCost: currentProductAddonCost,
      newAddonCost: tempProductAddonCost,
      currentPrice,
      newPrice,
      currentTotal,
      newTotal,
      priceDifference,
      percentChange: currentTotal > 0 ? (priceDifference / currentTotal) * 100 : 0
    };
  }
  
  // Get the quantity for this area
  const areaQuantity = areaData?.area || 1;
  
  return (
  <>
    {/* Overlay */}
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"></div>
    
    {/* Popup - positioned at bottom with slide-up animation */}
    <div className="fixed inset-x-0 bottom-0 bg-white rounded-t-xl shadow-lg z-50 max-h-[90vh] overflow-y-auto transform transition-transform duration-300 ease-out">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-3 py-2 flex justify-between items-center z-10 rounded-t-xl">
        {/* Left side - empty to maintain spacing */}
        <div className="w-6"></div>
        
        {/* Center - rectangle notch icon */}
        <div className="flex items-center justify-center">
          <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
        </div>
        
        {/* Right side - close button */}
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 p-1"
          disabled={isApplying}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Success Message */}
      {productApplied ? (
     <div className="p-6 flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-rose-50">
  <div className="relative mb-4">
    <div className="bg-gradient-to-r from-[#F30F0F] to-[#C20C0C] rounded-full h-16 w-16 flex items-center justify-center shadow-lg">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-sm border border-red-200">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-[#F30F0F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  </div>
  
  <h4 className="text-lg font-bold text-gray-800 mb-2">Product Successfully Applied!</h4>
  {tempSelectedProduct && (
    <p className="text-gray-600 text-center mb-4 max-w-md">
      <span className="font-medium text-[#F30F0F]">
        {tempSelectedProduct.item_name || tempSelectedProduct.product_group_name}
      </span> has been applied to <span className="font-medium">{areaName}</span>.
    </p>
  )}
  
  <div className="bg-white rounded-lg p-3 border border-red-100 shadow-sm mb-4">
    <p className="text-xs text-[#F30F0F] font-medium">✓ Product updated in your quotation</p>
  </div>
  
  <button
    onClick={onClose}
    className="px-6 py-2.5 bg-gradient-to-r from-[#F30F0F] to-[#C20C0C] text-white font-medium rounded-lg hover:from-[#E00E0E] hover:to-[#B00B0B] transition-all duration-200 shadow-lg"
  >
    Done
  </button>
</div>

      ) : (
        <>
          <div className="p-4">
            {/* Area header with door count and measurements */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="font-medium">{areaName}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600 space-x-3">
                <span>{areaData?.count || 1} {areaData?.count === 1 ? 'Door' : 'Doors'}</span>
                <span>•</span>
                <span>{areaData?.area || 0} {areaData?.UOM || "sq.ft"}</span>
              </div>
            </div>
            
            {/* Unified product selection and comparison card */}
            {currentProduct && (
              <div className={`border rounded-lg overflow-hidden mb-3 ${
                tempSelectedProduct && tempSelectedProduct._id !== currentProduct._id
                  ? priceComparison?.priceDifference > 0
                    ? 'border-red-200'
                    : priceComparison?.priceDifference < 0
                      ? 'border-green-200'
                      : 'border-blue-200'
                  : 'border-blue-200'
              }`}>
                {/* Current product section - always visible */}
                <div className="bg-white p-3">
                  <div className="flex items-center mb-1">
                    <div className="bg-blue-100 rounded-full p-1 mr-2 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="text-xs text-gray-500 font-medium">
                      {tempSelectedProduct && tempSelectedProduct._id !== currentProduct._id ? 'Prev Selection' : 'Selected Door'}
                    </div>
                  </div>
                  
                  <div className="flex items-start mt-1">
                    {currentProduct.vendor?.image && (
                      <div className="flex-shrink-0 border border-gray-200 rounded-md p-1 bg-white mr-2 mt-0.5">
                        <img
                          src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + currentProduct.vendor.image}
                          alt={currentProduct.vendor?.name || "Vendor"}
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800">
                        {currentProduct.item_name || currentProduct.product_group_name || "Item Name"}
                      </div>
                      
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-xs text-gray-600">
                          <span className="font-medium text-blue-700">
                            ₹{formatCurrency(getPriceBasedOnMoq(currentProduct, areaQuantity, data.items) + currentProductAddonCost)}
                          </span>/{currentProduct.UOM?.UOM_name || currentProduct.UOM || "sq.ft"}
                        </div>
                        
                        <div className="text-xs text-gray-600">
                          Total: <span className="font-medium text-blue-700">
                            ₹{formatCurrency((getPriceBasedOnMoq(currentProduct, areaQuantity, data.items) + currentProductAddonCost) * currentProduct.quantity)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* New product comparison section - only visible when a different product is selected */}
                {tempSelectedProduct && tempSelectedProduct._id !== currentProduct._id && priceComparison && (
                  <>
                    {/* New product comparison section - only visible when a different product is selected */}
{tempSelectedProduct && tempSelectedProduct._id !== currentProduct._id && priceComparison && (
  <>
    {/* Divider with price change indicator */}
    <div className={`flex items-center px-3 py-1.5 ${
      priceComparison.priceDifference > 0
        ? 'bg-red-50'
        : priceComparison.priceDifference < 0
          ? 'bg-green-50'
          : 'bg-blue-50'
    }`}>
      <div className={`rounded-full p-1 mr-2 ${
        priceComparison.priceDifference > 0
          ? 'bg-red-100'
          : priceComparison.priceDifference < 0
            ? 'bg-green-100'
            : 'bg-blue-100'
      }`}>
        {priceComparison.priceDifference > 0 ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        ) : priceComparison.priceDifference < 0 ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m-8 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        )}
      </div>
      <span className="text-xs font-medium text-gray-600">New Selection</span>
    </div>
    
    {/* New product details */}
    <div className="bg-white p-3">
      <div className="flex items-start">
        {tempSelectedProduct.vendor?.image && (
          <div className="flex-shrink-0 border border-gray-200 rounded-md p-1 bg-white mr-2 mt-0.5">
            <img
              src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + tempSelectedProduct.vendor.image}
              alt={tempSelectedProduct.vendor?.name || "Vendor"}
              className="w-6 h-6 object-contain"
            />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-800">
            {tempSelectedProduct.item_name || tempSelectedProduct.product_group_name || "Item Name"}
          </div>
          
          <div className="flex items-center justify-between mt-2">
            <div className="text-xs text-gray-600">
              <span className={`font-medium ${
                priceComparison.priceDifference > 0
                  ? 'text-red-600'
                  : priceComparison.priceDifference < 0
                    ? 'text-green-600'
                    : 'text-blue-700'
              }`}>₹{formatCurrency(priceComparison.newPrice)}</span>/{tempSelectedProduct.UOM?.UOM || tempSelectedProduct.UOM || "sq.ft"}
            </div>
            
            <div className="text-xs text-gray-600">
              Total: <span className={`font-medium ${
                priceComparison.priceDifference > 0
                  ? 'text-red-600'
                  : priceComparison.priceDifference < 0
                    ? 'text-green-600'
                    : 'text-blue-700'
              }`}>₹{formatCurrency(priceComparison.newTotal)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Price difference summary */}
    <div className={`p-3 ${
      priceComparison.priceDifference > 0
        ? 'bg-red-50'
        : priceComparison.priceDifference < 0
          ? 'bg-green-50'
          : 'bg-blue-50'
    }`}>
      {/* Main price difference message - conversational style */}
      <div className="flex items-start mb-2">
        <div className={`rounded-full p-1 mr-2 flex-shrink-0 mt-0.5 ${
          priceComparison.priceDifference > 0
            ? 'bg-red-100'
            : priceComparison.priceDifference < 0
              ? 'bg-green-100'
              : 'bg-blue-100'
        }`}>
          {priceComparison.priceDifference > 0 ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          ) : priceComparison.priceDifference < 0 ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        
        <p className="text-sm">
          It will {priceComparison.priceDifference > 0 ? 'cost you' : 'save you'}
          <span className={`font-bold mx-1 ${
            priceComparison.priceDifference > 0
              ? 'text-red-600'
              : priceComparison.priceDifference < 0
                ? 'text-green-600'
                : 'text-blue-600'
          }`}>
            ₹{formatCurrency(Math.abs(priceComparison.priceDifference))}
          </span>
          {priceComparison.priceDifference > 0 ? 'more' : 'less'} by choosing
          <span className="font-medium mx-1">
            {tempSelectedProduct.item_name || tempSelectedProduct.product_group_name}
          </span>
          for
          <span className="font-medium mx-1">
            {areaName}
          </span>
          {areaData?.count > 1 && ` (${areaData.count} doors)`}.
        </p>
      </div>
      
     
    </div>
  </>
)}

                  </>
                )}
              </div>
            )}
          </div>
          
        
           {/* Products List */}
<div className="p-4">
  <h4 className="text-sm font-medium text-gray-700 mb-3">Available Products</h4>
  
  {areaData?.doorTypes?.[selectedDoorType]?.length > 0 ? (
    <div className="grid grid-cols-1 gap-3">
      {areaData.doorTypes[selectedDoorType].map((item, index) => {
        // Only show the current product as selected if no temporary selection is made
        const isCurrentlySelected = currentProductId === item.item_group_id && !tempSelectedProductId;
        const isTemporarilySelected = tempSelectedProductId === item.item_group_id;
        const quantityByUOM = item.UOM.toLowerCase() === 'no' ? areaData.count : areaData.area;
        
        // Calculate addon costs for this product
        const itemAddonCost = calculateAddonCost(item);
        const totalItemPrice = (item.selling_price || 0) + itemAddonCost;
        
        return (
          <div
            key={index}
            className={`border rounded-lg p-3 cursor-pointer transition-all ${
              isTemporarilySelected
                ? 'border-red-500 bg-red-50'
                : isCurrentlySelected
                  ? 'border-blue-300 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => onProductSelect(item)}
          >
            <div className="flex items-start">
              {/* Selection indicator */}
              <div className="mr-3 mt-1">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  isTemporarilySelected
                    ? 'border-red-500 bg-red-500'
                    : isCurrentlySelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                }`}>
                  {(isTemporarilySelected || isCurrentlySelected) && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              
              {/* Product content */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1 min-w-0">
                    {/* Vendor logo */}
                    {item.vendor?.image && (
                      <div className="flex-shrink-0 border border-gray-200 rounded-md p-0.5 bg-white mr-2 mt-0.5">
                        <img
                          src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + item.vendor.image}
                          alt={item.vendor?.name || "Vendor"}
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                    )}
                    
                    {/* Product name */}
                    <h5 className="text-sm font-medium text-gray-800 line-clamp-2 pr-2 flex-1">
                      {item.item_name || item.product_group_name || "Item Name"}
                    </h5>
                  </div>
                  
                  {/* Price badge - now includes addon costs */}
                  <div className="bg-[#F0FFF0] text-[#14A311] text-xs font-medium px-2 py-1 rounded-md flex items-center ml-2 whitespace-nowrap flex-shrink-0">
                    <span>₹{formatCurrency(totalItemPrice)}</span>
                    <span className="mx-1 opacity-70">/</span>
                    <span>{item.UOM?.UOM_name || item.UOM || "sq.ft"}</span>
                  </div>
                </div>
                
                {/* Added: Total price based on quantity */}
                <div className="mt-2 flex justify-between items-center">
                  <div className="text-xs text-gray-600 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                    <span>Quantity: {quantityByUOM} {item.UOM?.UOM || item.UOM || "sq.ft"}</span>
                  </div>
                  <div className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-md flex items-center">
                    <span className="mr-1">Total:</span>
                    <span className="text-[#14A311] font-semibold">
                      ₹{formatCurrency(totalItemPrice * (quantityByUOM || 0))}
                    </span>
                  </div>
                </div>
                
                {/* Price comparison with current selection */}
                {currentProduct && currentProductId !== item.item_group_id && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    {(() => {
                      const currentTotalPrice = getPriceBasedOnMoq(currentProduct, areaQuantity) + currentProductAddonCost;
                      const newTotalPrice = totalItemPrice;
                      const priceDiff = newTotalPrice - currentTotalPrice;
                      const percentChange = currentTotalPrice > 0 ? (priceDiff / currentTotalPrice) * 100 : 0;
                      
                      if (priceDiff === 0) {
                        return (
                          <div className="text-xs text-gray-500 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Same price as current selection
                          </div>
                        );
                      }
                      
                      return (
                        <div className={`text-xs flex items-center ${
                          priceDiff > 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {priceDiff > 0 ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                              <span>Costs ₹{formatCurrency(Math.abs(priceDiff))} more per {item.UOM} ({percentChange.toFixed(1)}%)</span>
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                              </svg>
                              <span>Saves ₹{formatCurrency(Math.abs(priceDiff))} per unit ({Math.abs(percentChange).toFixed(1)}%)</span>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                
              </div>
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg border border-gray-200">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
      <p className="text-gray-600 font-medium">No products available</p>
      {Object.keys(areaData?.doorTypes || {}).length > 1 && (
        <p className="text-gray-500 text-sm mt-1">Try selecting a different door type</p>
      )}
    </div>
  )}
</div>

          
          {/* Footer with action buttons - now sticky at bottom */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 shadow-lg">
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                disabled={isApplying}
              >
                Cancel
              </button>
              
              <button
                onClick={onApply}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 ${
                  tempSelectedProductId
                    ? 'bg-gradient-to-r from-[#F30F0F] to-[#C20C0C] text-white hover:from-[#E00E0E] hover:to-[#B00B0B] shadow-lg'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!tempSelectedProductId || isApplying}
              >
                {isApplying ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Applying...
                  </>
                ) : (
                  'Apply Selection'
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  </>
);

  
};

// Main Component
const DoorsApplicationAreaMapping = ({
  data,
  onProductSelect,
  onlyDoorProducts,
  preEngDoorProducts,
  doorSubcategories,
}) => {
  // State variables
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedProducts, setSelectedProducts] = useState({});
  const [isProductSelectionPopupOpen, setIsProductSelectionPopupOpen] = useState(false);
  const [currentArea, setCurrentArea] = useState('');
  const [currentAreaData, setCurrentAreaData] = useState(null);
  const [selectedDoorTypeInPopup, setSelectedDoorTypeInPopup] = useState('');
  const [selectedDoorType, setSelectedDoorType] = useState(null);
  const [tempSelectedProductId, setTempSelectedProductId] = useState(null);
  const [tempSelectedProduct, setTempSelectedProduct] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [productApplied, setProductApplied] = useState(false);
  const [areaQuantities, setAreaQuantities] = useState({});
  const [showAllOptimizations, setShowAllOptimizations] = useState(false);
  
  // Animation styles
  const animations = `
    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
    .animate-slide-up {
      animation: slideUp 0.3s ease-out forwards;
    }
    
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
    .animate-fade-in {
      animation: fadeIn 0.2s ease-out forwards;
    }
  `;

  // Initialize data on component mount
  useEffect(() => {
    if (data?.items && Array.isArray(data.items)) {
      // Initialize expanded sections
      const initialExpandedState = {};
      
      // Group items by area name for initialization
      const areaGroups = {};
      const initialAreaQuantities = {};
      
      data.items.forEach(item => {
        if (item.application_areas && item.application_areas.area_name) {
          const areaName = item.application_areas.area_name;
          
          // Initialize area in tracking objects
          if (!areaGroups[areaName]) {
            areaGroups[areaName] = [];
            initialExpandedState[areaName] = true;
            
            // Store the quantity for this area
            if (item.application_areas.count) {
              initialAreaQuantities[areaName] = item.quantity;
            }
          }
          
          areaGroups[areaName].push(item);
          
          // Set expanded state for both door types by default
          initialExpandedState[`${areaName}-Only Doors`] = true;
          initialExpandedState[`${areaName}-Pre-Engineered Doors`] = false; // Start with only one expanded
        }
      });
      
      setExpandedSections(initialExpandedState);
      setAreaQuantities(initialAreaQuantities);
      
      // Initialize selected products from existing data
      const initialSelectedProducts = {};
      data.items.forEach(item => {
        if (item.application_areas && item.application_areas.area_name) {
          initialSelectedProducts[item.application_areas.area_name] = item.item_group_id || item.item_id;
        }
      });
      setSelectedProducts(initialSelectedProducts);
    }
  }, [data]);

  // Helper function to toggle section expansion
  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

 

  const getPriceBasedOnMoq = (product, quantity, allItems) => {
  // Check if product has valid MOQ data
  if (!product || !product.moq || !product.moq.moqs || !Array.isArray(product.moq.moqs)) {
    return product?.selling_price || 0;
  }
  
  // Calculate total quantity of this product across the entire quotation
  let totalQuantity = quantity;
  
  // If allItems is provided, find all instances of this product and sum their quantities
  if (allItems && Array.isArray(allItems)) {
    const sameProductItems = allItems.filter(item => 
      item.item_group_id === product._id 
    );
    
    if (sameProductItems.length > 0) {
      // Sum up quantities from all instances of this product
      const existingQuantity = sameProductItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
     // console.log("Existing Quantity:", existingQuantity);
      // Add the existing quantity to our current quantity
      totalQuantity = existingQuantity ;
    }
  }
  
 // Find the applicable MOQ tier based on total quantity
let applicableMoq = null;

// Check each MOQ to find the applicable one
for (const moq of product.moq.moqs) {
  // Check if quantity is within this MOQ range
  if (totalQuantity >= moq.min_quantity) {
    // If max_quantity is not defined or null, or if quantity is within range
    if (moq.max_quantity === null || moq.max_quantity === undefined || totalQuantity <= moq.max_quantity) {
      // If we haven't found an applicable MOQ yet, or this one has a higher min_quantity
      // (meaning it's a more specific tier), use this one
      if (!applicableMoq || moq.min_quantity > applicableMoq.min_quantity) {
        applicableMoq = moq;
      }
    }
  }
}
//console.log("Applicable MOQ:", applicableMoq);
// Return the appropriate price or default to the product's selling price
return applicableMoq ? applicableMoq.selling_price : product.selling_price;

};

 
  // Helper function to calculate addon cost for a product
  const calculateAddonCost = (product) => {
    if (!product || !product.addons || !Array.isArray(product.addons)) {
      return 0;
    }
    
    return product.addons.reduce((addonTotal, addon) => {
      if (!addon || !addon.addon_groups || !Array.isArray(addon.addon_groups)) {
        return addonTotal;
      }
      
      return addonTotal + addon.addon_groups.reduce((groupTotal, addonGroup) => {
        if (!addonGroup || !addonGroup.products || !Array.isArray(addonGroup.products)) {
          return groupTotal;
        }
        
        // Sum up the prices of all products in this addon group
        const groupCost = addonGroup.products.reduce((productTotal, addonProduct) => {
          return productTotal + (addonProduct.selling_price || 0);
        }, 0);
        
        return groupTotal + groupCost;
      }, 0);
    }, 0);
  };


  // Function to handle product selection
  const handleProductSelect = (areaName, productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [areaName]: productId
    }));
    
    if (onProductSelect) {
      // Find the selected product
      let selectedProduct = null;
      
      // Look in onlyDoorProducts
      if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
        selectedProduct = onlyDoorProducts.find(p => p._id === productId);
      }
      
      // If not found, look in preEngDoorProducts
      if (!selectedProduct && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
        selectedProduct = preEngDoorProducts.find(p => p._id === productId);
      }
      
      // Get the quantity for this area
      const quantity = areaQuantities[areaName] || 1;
       console.log("Quantity:", quantity);
      // Calculate the price based on MOQ
      console.log("Selected Product:", selectedProduct);

      const price = getPriceBasedOnMoq(selectedProduct, quantity , data.items);
      
      // Calculate addon cost
      const addonCost = calculateAddonCost(selectedProduct);
      
           // Pass the selected product ID, area name, calculated price, and addon cost to the parent component
      onProductSelect(areaName, productId, price, addonCost);
    }
  };

  // Function to handle temporary product selection in the popup
  const handleTempProductSelect = (product) => {
    setTempSelectedProductId(product.item_group_id);
    setTempSelectedProduct(product);
  };

  // Function to apply the selected product
  const applyProductSelection = async () => {
    if (!tempSelectedProductId || !currentArea) return;
    
    // Start loading state
    setIsApplying(true);
    
    try {
      // Simulate API call or processing time (remove this in production)
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Apply the product selection
      handleProductSelect(currentArea, tempSelectedProductId);
      
      // Show success state
      setProductApplied(true);
      
    } catch (error) {
      console.error('Error applying product selection:', error);
      // Handle error if needed
    } finally {
      // End loading state
      setIsApplying(false);
    }
  };

  // Function to open the product selection popup
  const openProductSelectionPopup = (areaName, selectedDoorType) => {
    setCurrentArea(areaName);
    setSelectedDoorType(selectedDoorType);
    // Find the area data for the selected area
    const areaData = areasList.find(area => area.area_name === areaName);
    setCurrentAreaData(areaData);
    
    // Reset states
    setProductApplied(false);
    setTempSelectedProductId(null);
    setTempSelectedProduct(null);
    
    // Set the default door type to show in the popup
    if (areaData && Object.keys(areaData.doorTypes).length > 0) {
      setSelectedDoorTypeInPopup(Object.keys(areaData.doorTypes)[0]);
    }
    
    setIsProductSelectionPopupOpen(true);
  };

  // Function to close the product selection popup
  const closeProductSelectionPopup = () => {
    setIsProductSelectionPopupOpen(false);
    setTempSelectedProductId(null);
    setTempSelectedProduct(null);
    setProductApplied(false);
    setIsApplying(false);
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Extract unique application areas from the quotation data
  const applicationAreas = {};
  
  // First, collect all unique areas from the quotation items
  if (data?.items && Array.isArray(data.items)) {
    data.items.forEach(item => {
      if (item.application_areas && item.application_areas.area_name) {
        const areaName = item.application_areas.area_name;
        
        if (!applicationAreas[areaName]) {
          applicationAreas[areaName] = {
            area_name: areaName,
            area: item.application_areas.area,
            count: item.application_areas.count,
            doorTypes: {
              'Only Doors': [],
              'Pre-Engineered Doors': []
            }
          };
        }
      }
    });
  }
  
  // Now map the door products to each area
  if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
   
    // For each area, add all the door products as options
    Object.keys(applicationAreas).forEach(areaName => {
      const area = applicationAreas[areaName];
      const quantity = area.count || 1;
      
      // Add all onlyDoorProducts to this area
      onlyDoorProducts.forEach(product => {
        // Calculate price based on MOQ and quantity
        const calculatedPrice = getPriceBasedOnMoq(product, quantity, data.items);
        area.doorTypes['Only Doors'].push({
          ...product,
          item_id: product._id,
          item_name: product.product_group_name,
          item_group_id: product._id,
          selling_price: calculatedPrice, // Use calculated price based on MOQ
          original_price: product.selling_price, // Keep original price for reference
          currentQuantity: quantity, // Store the current quantity
          application_areas: { area_name: areaName },
          isProductOption: true
        });
      });
    });
  }
  
  // Map Pre-Engineered Door products if available
  if (preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
    Object.keys(applicationAreas).forEach(areaName => {
      const area = applicationAreas[areaName];
      const quantity = area.count || 1;
      
      preEngDoorProducts.forEach(product => {
        // Calculate price based on MOQ and quantity
        const calculatedPrice = getPriceBasedOnMoq(product, quantity, data.items);
        
        area.doorTypes['Pre-Engineered Doors'].push({
          ...product,
          item_id: product._id,
          item_name: product.product_group_name,
          item_group_id: product._id,
          selling_price: calculatedPrice, // Use calculated price based on MOQ
          original_price: product.selling_price, // Keep original price for reference
          currentQuantity: quantity, // Store the current quantity
          UOM: product.UOM || "sq.ft",
          application_areas: { area_name: areaName },
          isProductOption: true
        });
      });
    });
  }
  
  // Mark currently selected products in the quotation
  if (data?.items && Array.isArray(data.items)) {
    data.items.forEach(item => {
      if (item.application_areas && item.application_areas.area_name) {
        const areaName = item.application_areas.area_name;
        const area = applicationAreas[areaName];
        
        if (area) {
          // Determine door type based on subcategory or other criteria
          // For simplicity, we'll check if it's in onlyDoorProducts or preEngDoorProducts
          let doorType = 'Only Doors'; // Default
          
          // Find the matching product in the door types
          let foundInOnlyDoors = false;
          let foundInPreEngDoors = false;
          
          if (item.item_group_id) {
            // Check if this item is in onlyDoorProducts
            if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
              foundInOnlyDoors = onlyDoorProducts.some(p => p._id === item.item_group_id);
            }
            
            // Check if this item is in preEngDoorProducts
            if (preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
              foundInPreEngDoors = preEngDoorProducts.some(p => p._id === item.item_group_id);
            }
            
            if (foundInPreEngDoors) {
              doorType = 'Pre-Engineered Doors';
            }
          }
          
          // Mark this item as selected in the UI
          const existingProductIndex = area.doorTypes[doorType].findIndex(
            p => p.item_group_id === item.item_group_id
          );
          
          if (existingProductIndex >= 0) {
            area.doorTypes[doorType][existingProductIndex].isSelected = true;
          }
        }
      }
    });
  }

  const areasList = Object.values(applicationAreas || {});

   const getCombinations = (array, k) => {
    const result = [];
    
    // Helper function to generate combinations
    const generateCombinations = (start, combination) => {
      if (combination.length === k) {
        result.push([...combination]);
        return;
      }
      
      for (let i = start; i < array.length; i++) {
        combination.push(array[i]);
        generateCombinations(i + 1, combination);
        combination.pop();
      }
    };
    
    generateCombinations(0, []);
    return result;
  };


  // Calculate optimization suggestions
  const moqOptimizationSuggestions = useMemo(() => {
    const suggestions = [];
    
    // Skip if no products are selected
    if (Object.keys(selectedProducts).length === 0) {
      return suggestions;
    }
    
    // Get all unique product IDs that are currently selected
    const uniqueProductIds = [...new Set(Object.values(selectedProducts))];
    
    // For each unique product, check if using it in more areas would save money
    uniqueProductIds.forEach(productId => {
      // Find the product details
      let product = null;
      if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
        product = onlyDoorProducts.find(p => p._id === productId);
      }
      if (!product && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
        product = preEngDoorProducts.find(p => p._id === productId);
      }
      
      if (!product || !product.moq || !product.moq.moqs || product.moq.moqs.length <= 1) {
        return; // Skip if product doesn't have MOQ tiers
      }
      
      // Get current areas using this product
      const currentAreas = Object.entries(selectedProducts)
        .filter(([_, selectedId]) => selectedId === productId)
        .map(([areaName]) => areaName);
      
      // Calculate current total quantity and price
      const currentTotalQuantity = currentAreas.reduce((total, areaName) => {
        return total + (areaQuantities[areaName] || 1);
      }, 0);
      
      const currentPrice = getPriceBasedOnMoq(product, currentTotalQuantity , data.items);
      
      // Find areas not using this product
      const otherAreas = Object.keys(selectedProducts).filter(areaName => !currentAreas.includes(areaName));
      
      // For each possible combination of other areas, check if adding them would reduce the price
      for (let i = 1; i <= otherAreas.length; i++) {
        // Get all possible combinations of i areas
        const combinations = getCombinations(otherAreas, i);
        
        combinations.forEach(areasToAdd => {
          // Calculate new total quantity if we add these areas
          const additionalQuantity = areasToAdd.reduce((total, areaName) => {
            return total + (areaQuantities[areaName] || 1);
          }, 0);
          
          const newTotalQuantity = currentTotalQuantity + additionalQuantity;
          const newPrice = getPriceBasedOnMoq(product, newTotalQuantity, data.items);
          
          // If the new price is lower, this is a potential optimization
          if (newPrice < currentPrice) {
            // Calculate current cost for the areas that would change
            let currentCostForChangingAreas = 0;
            const areasToChange = [];
            
            areasToAdd.forEach(areaName => {
              const currentProductId = selectedProducts[areaName];
              let currentAreaProduct = null;
              
              // Find the current product for this area
              if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
                currentAreaProduct = onlyDoorProducts.find(p => p._id === currentProductId);
              }
              if (!currentAreaProduct && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
                currentAreaProduct = preEngDoorProducts.find(p => p._id === currentProductId);
              }
              
              if (currentAreaProduct) {
                const areaQuantity = areaQuantities[areaName] || 1;
                const currentAreaPrice = getPriceBasedOnMoq(currentAreaProduct, areaQuantity , data.items);
                currentCostForChangingAreas += currentAreaPrice * areaQuantity;
                areasToChange.push(areaName);
              }
            });
            
            // Calculate new cost for the areas that would change
            const newCostForChangingAreas = newPrice * additionalQuantity;
            
            // Calculate potential savings
            const potentialSavings = currentCostForChangingAreas - newCostForChangingAreas;
            
            if (potentialSavings > 0) {
              // Calculate addon differences
              const addonDifference = {};
              
              areasToChange.forEach(areaName => {
                const currentProductId = selectedProducts[areaName];
                let currentAreaProduct = null;
                
                // Find the current product for this area
                if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
                  currentAreaProduct = onlyDoorProducts.find(p => p._id === currentProductId);
                }
                if (!currentAreaProduct && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
                  currentAreaProduct = preEngDoorProducts.find(p => p._id === currentProductId);
                }
                
                // Compare addons between current product and suggested product
                if (currentAreaProduct) {
                  const currentAddonCost = calculateAddonCost(currentAreaProduct);
                  const suggestedAddonCost = calculateAddonCost(product);
                  
                  addonDifference[areaName] = {
                    currentAddonCost,
                    suggestedAddonCost,
                    difference: suggestedAddonCost - currentAddonCost
                  };
                }
              });
              
              // Add to suggestions
              suggestions.push({
                productId,
                productName: product.product_group_name || product.item_name,
                vendor: product.vendor,
                currentPrice,
                potentialPrice: newPrice,
                currentQuantity: currentTotalQuantity,
                potentialQuantity: newTotalQuantity,
                areasToChange,
                potentialSavings,
                addonCost: calculateAddonCost(product),
                addonDifference,
                standardProduct: product
              });
            }
          }
        });
      }
    });
    
    // Sort suggestions by potential savings (highest first)
    return suggestions.sort((a, b) => b.potentialSavings - a.potentialSavings);
  }, [selectedProducts, areaQuantities, onlyDoorProducts, preEngDoorProducts]);

  // Helper function to get all combinations of k elements from an array
 
  // Function to apply an optimization suggestion
  const applyOptimizationSuggestion = (suggestion) => {
    // Apply the suggested product to all areas that should change
    suggestion.areasToChange.forEach(areaName => {
      handleProductSelect(areaName, suggestion.productId);
    });
  };

  return (
    <div className=" mb-15">
      <style>{animations}</style>
      
      {/* Price Optimization Banner */}
      <PriceOptimizationBanner
        suggestions={moqOptimizationSuggestions}
        onApplySuggestion={applyOptimizationSuggestion}
        formatCurrency={formatCurrency}
        selectedProducts={selectedProducts}
        onlyDoorProducts={onlyDoorProducts}
        preEngDoorProducts={preEngDoorProducts}
      />
        <div className="px-4 mb-4">
  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#C20C0C]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m-8 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
    Change Selections
  </h3>
</div>

      
      {areasList.map((area, areaIndex) => {
        // Get the selected product for this area
        const selectedProductId = selectedProducts[area.area_name];
                // Find the selected product data from all door types
        let selectedProduct = null;
        let selectedDoorType = null;
        
        // Search through all door types to find the selected product
        Object.entries(area.doorTypes).forEach(([doorType, items]) => {
          const found = items.find(item => item.item_group_id === selectedProductId);
       
          if (found) {
            selectedProduct = found;
            selectedDoorType = doorType;
          }
        });
        
        return (

      <div key={areaIndex} className="shadow-md bg-white border border-gray-200 rounded-lg overflow-hidden mt-4">
  {/* Area Header */}
  <div className="px-2 py-2">
  <div className="mb-2 flex flex-row justify-between items-center p-1">
    <div className="flex items-start space-x-2 mr-1 w-full">
      {/* Area icon/image - similar to vendor image in tiles */}
      <div className="border border-gray-200 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
        {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg> */}
        <svg 
  xmlns="http://www.w3.org/2000/svg" 
  className="h-8 w-8 text-gray-500" 
  fill="none" 
  viewBox="0 0 24 24" 
  stroke="currentColor"
>
  <rect x="4" y="2" width="16" height="20" rx="1" />
  <line x1="12" y1="2" x2="12" y2="22" />
  <line x1="16" y1="12" x2="18" y2="12" />
</svg>

      </div>
      
      {/* Area details container */}
      <div className="flex-1 min-w-0">
        {/* Area name with styling similar to vendor name in tiles */}
        <h5 className="text-[#3C5DB1] font-semibold leading-tight mb-1">
          <span className="text-red-500 text-lg font-semibold line-clamp-2">{area.area_name}</span>
        </h5>
        
        {/* Door type badge - similar to attributes badges in tiles */}
        <div className="flex flex-row items-center gap-1 mt-1">
          {selectedDoorType && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
              {selectedDoorType.toLowerCase() === 'pre eng doors'
                ? 'Doors with Frame'
                : selectedDoorType.toLowerCase() === 'only doors'
                  ? 'Doors without Frame'
                  : 'Doors with Frame'
              }
            </span>
          )}
        </div>
      </div>
    </div>
    
    {/* Area measurements - adjusted to have area and sq.ft in row, count and doors in row */}
    <div className="flex flex-col items-end">
      <div className="flex flex-row space-x-1 items-center mb-1">
          <span className="font-bold text-xs text-gray-600">{area?.area || 0}</span> 
        <span className="text-xs text-gray-500">sq.ft</span>
      </div>
      <div className="flex flex-row space-x-1  items-center">
          <span className="font-bold text-xs text-gray-600">{area?.count || 0}</span> 
        <span className="text-xs text-gray-500"> {area?.count === 1 ? 'Door' : 'Doors'} </span>
      </div>
    </div>
  </div>
</div>

  
  {/* Selected Product Display or Empty State - keeping this part the same */}
  <div className="px-4 py-2 border-t border-gray-200">
    {selectedProduct ? (
      <SelectedProductDisplay
        product={selectedProduct}
        formatCurrency={formatCurrency}
        onChangeClick={() => openProductSelectionPopup(area.area_name, selectedDoorType)}
        doorSubcategories={doorSubcategories}
      />
    ) : (
      <EmptyProductState
        onSelectClick={() => openProductSelectionPopup(area.area_name, selectedDoorType)}
      />
    )}
  </div>
</div>

        );
      })}
      
      {/* Empty state when no areas are available */}
      {areasList.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="bg-gray-100 rounded-full p-3 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">No Door Areas Found</h3>
            <p className="text-gray-500 text-center mb-6 max-w-md">
              It looks like there are no door areas defined in this project. Please add door areas to continue.
            </p>
            <button className="px-6 py-2.5 bg-[#C20C0C] text-white font-medium rounded-lg hover:bg-[#A00A0A] transition-colors">
              Add Door Areas
            </button>
          </div>
        </div>
      )}
      
      {/* Product Selection Popup */}
<ProductSelectionPopup
  isOpen={isProductSelectionPopupOpen}
  onClose={closeProductSelectionPopup}
  areaName={currentArea}
  areaData={currentAreaData}
  selectedDoorType={selectedDoorType}
  onDoorTypeChange={setSelectedDoorTypeInPopup}
  tempSelectedProductId={tempSelectedProductId}
  onProductSelect={handleTempProductSelect}
  onApply={applyProductSelection}
  isApplying={isApplying}
  productApplied={productApplied}
  formatCurrency={formatCurrency}
  selectedProducts={selectedProducts}
  getPriceBasedOnMoq={getPriceBasedOnMoq}
  onlyDoorProducts={onlyDoorProducts}
  preEngDoorProducts={preEngDoorProducts}
  doorSubcategories={doorSubcategories}
  data={data}
/>
     
      
      {/* Price Comparison Modal */}
      {showAllOptimizations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto animate-fade-in">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">Price Comparison</h3>
              <button
                onClick={() => setShowAllOptimizations(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Area
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Product
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(selectedProducts).map(([areaName, productId], index) => {
                      // Find the product details
                      let product = null;
                      if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
                        product = onlyDoorProducts.find(p => p._id === productId);
                      }
                      if (!product && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
                        product = preEngDoorProducts.find(p => p._id === productId);
                      }
                      
                      const quantity = areaQuantities[areaName] || 1;
                      const price = getPriceBasedOnMoq(product, quantity, data.items);
                      const total = price * quantity;
                      
                      return (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {areaName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {product ? product.product_group_name : 'Unknown Product'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ₹{formatCurrency(price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ₹{formatCurrency(total)}
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* Total row */}
                    <tr className="bg-gray-100">
                      <td colSpan="4" className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        Total
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₹{formatCurrency(
                          Object.entries(selectedProducts).reduce((total, [areaName, productId]) => {
                            // Find the product details
                            let product = null;
                            if (onlyDoorProducts && Array.isArray(onlyDoorProducts)) {
                              product = onlyDoorProducts.find(p => p._id === productId);
                            }
                            if (!product && preEngDoorProducts && Array.isArray(preEngDoorProducts)) {
                              product = preEngDoorProducts.find(p => p._id === productId);
                            }
                            
                            const quantity = areaQuantities[areaName] || 1;
                            const price = getPriceBasedOnMoq(product, quantity, data.items);
                            return total + (price * quantity);
                          }, 0)
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end">
              <button
                onClick={() => setShowAllOptimizations(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoorsApplicationAreaMapping;


